import React, { useState } from "react";
import { X, CaretLeft, CaretRight, BookOpen, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const STEPS = [
  {
    title: "Welcome to the Content Libraries",
    icon: BookOpen,
    body: (
      <>
        <p>The Libraries are the single source of truth for everything that goes into a proposal. Every proposal your sales team creates is <b>composed</b> from these library entries — never free-typed.</p>
        <p className="mt-3">There are five libraries, one per tab at the top of this page:</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li><b>Features</b> — the capabilities you sell (e.g. Footfall Counting, Queue Analytics)</li>
          <li><b>Clauses</b> — reusable legal/commercial paragraphs (Confidentiality, Hardware Conformance)</li>
          <li><b>Price Book</b> — every charge with a list price and floor price</li>
          <li><b>Suite Templates</b> — bundles of features (e.g. "Retail Store Baseline")</li>
          <li><b>Prerequisites</b> — things the client or vendor must have in place</li>
        </ul>
        <p className="mt-3 text-slate-600 text-sm">Only users with the <b>admin</b> role can add, edit or delete. Everyone else sees them as read-only reference.</p>
      </>
    ),
  },
  {
    title: "How this affects your proposals",
    icon: BookOpen,
    body: (
      <>
        <p>Changes here flow through <b>everything</b>:</p>
        <ul className="mt-2 space-y-1.5 text-sm list-disc list-inside">
          <li>Rename a feature → new proposals show the new name automatically.</li>
          <li>Update a price → new proposals compute totals using the new price.</li>
          <li>Add a new clause → sales users can insert it into the correct section.</li>
        </ul>
        <p className="mt-3 text-sm">Proposals already <b>issued</b> are frozen (versioned) — they never change even if you edit the library later. This protects sent documents.</p>
        <p className="mt-3 text-sm bg-amber-50 border-l-2 border-amber-500 p-2">⚠ Be careful when <b>deleting</b>: existing draft proposals that reference the entry will show blank fields. Prefer editing over deleting.</p>
      </>
    ),
  },
  {
    title: "Adding a Feature",
    icon: Plus,
    body: (
      <>
        <p>1. Go to the <b>Features</b> tab.</p>
        <p>2. Click the black <b>+ New feature</b> button on the top-right.</p>
        <p>3. Fill in these fields:</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li><b>Name</b> — client-facing name (e.g. "Heatmap Analytics").</li>
          <li><b>Module</b> — group it lives in ("Vision", "Analytics", etc.).</li>
          <li><b>Client outcome</b> — 1-2 sentences describing what the customer gets. This appears in the proposal's Scope section.</li>
          <li><b>Camera requirements</b> — hardware/angle notes for site surveys.</li>
          <li><b>Accuracy position</b> — how you talk about accuracy (kept "directional" to avoid over-commitment).</li>
          <li><b>Exclusion text</b> — what this feature explicitly does <i>not</i> cover; appears in the proposal's Scope Boundary.</li>
          <li><b>Compute class</b> — <span className="font-mono text-xs">gpu</span> or <span className="font-mono text-xs">cpu</span> (drives infra sizing).</li>
          <li><b>Requires approval</b> — check this if selling the feature needs approver sign-off before proposal can be issued.</li>
        </ul>
        <p className="mt-3">4. Click <b>Save</b>. The feature is now available in every new proposal's feature-picker.</p>
      </>
    ),
  },
  {
    title: "Editing or Deleting a Feature",
    icon: PencilSimple,
    body: (
      <>
        <p>Every row in a library table has two icons on the right:</p>
        <ul className="mt-2 space-y-2 text-sm">
          <li><span className="inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-0.5"><PencilSimple size={12}/></span> <b>Edit</b> — opens the same form with existing values. Change what you need and Save.</li>
          <li><span className="inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-0.5"><Trash size={12}/></span> <b>Delete</b> — asks for confirmation. Deletion is immediate and irreversible.</li>
        </ul>
        <p className="mt-3 text-sm bg-blue-50 border-l-2 border-blue-500 p-2">💡 If you just want to stop using a feature but keep it for old proposals, uncheck its verticals list (or add a temporary suffix like "(deprecated)") instead of deleting.</p>
      </>
    ),
  },
  {
    title: "Managing the Price Book",
    icon: BookOpen,
    body: (
      <>
        <p>Every priceable thing lives in the Price Book. Each row has:</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li><b>Charge code</b> — short unique identifier (e.g. <span className="font-mono text-xs">PILOT_FEE</span>). Used by the pricing engine.</li>
          <li><b>Label</b> — client-facing line description on the proposal.</li>
          <li><b>Charge type</b> — <span className="font-mono text-xs">one_time</span> (paid once), <span className="font-mono text-xs">recurring</span> (per month), or <span className="font-mono text-xs">usage</span>.</li>
          <li><b>Basis</b> — how quantity is counted: per_site, per_camera, per_project, etc.</li>
          <li><b>List price</b> — the standard price, in <b>paise</b>. So ₹2,50,000 → enter <span className="font-mono text-xs">25000000</span>.</li>
          <li><b>Floor price</b> — the lowest allowed price. Sales can drop to floor without approval; below floor requires an approver.</li>
        </ul>
        <p className="mt-3 text-sm bg-amber-50 border-l-2 border-amber-500 p-2">⚠ Enter prices in <b>paise</b> (rupees × 100). ₹500 = 50000. This avoids decimal rounding issues in totals.</p>
      </>
    ),
  },
  {
    title: "Managing Clauses",
    icon: BookOpen,
    body: (
      <>
        <p>Clauses are pre-approved paragraphs of prose — legal, commercial, or scope-boundary language.</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li><b>Key</b> — a stable identifier like <span className="font-mono text-xs">confidentiality</span>. Never change it after clauses go live.</li>
          <li><b>Title</b> — heading shown above the paragraph in the proposal.</li>
          <li><b>Section binding</b> — which proposal section the clause belongs to. Examples: <span className="font-mono text-xs">commercial_terms</span>, <span className="font-mono text-xs">assumptions</span>.</li>
          <li><b>Body</b> — the actual paragraph. Write it as it should appear to the client.</li>
          <li><b>Applicability</b> — <span className="font-mono text-xs">always</span> (default) or a condition like <span className="font-mono text-xs">has_refund</span> so the clause only shows when relevant.</li>
        </ul>
        <p className="mt-3 text-sm">Sales users cannot re-word clauses inside a proposal — they can only include or omit them. This keeps legal language consistent.</p>
      </>
    ),
  },
  {
    title: "Suite Templates & Prerequisites",
    icon: BookOpen,
    body: (
      <>
        <p><b>Suite Templates</b> are bundles of features you often sell together — like a menu combo.</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li>Give it a Name and Vertical.</li>
          <li>Check the features that make up the bundle.</li>
          <li>Sales can pick this suite as a starting point in a new proposal (feature list pre-selected).</li>
        </ul>
        <p className="mt-4"><b>Prerequisites</b> are things the client or vendor must have in place before deployment — CCTV infra, POS access, network availability, etc.</p>
        <ul className="mt-2 space-y-1.5 text-sm">
          <li><b>Key</b> — short unique id.</li>
          <li><b>Text</b> — the sentence that appears in the Prerequisites section.</li>
          <li><b>Category</b> — <span className="font-mono text-xs">client</span> (their responsibility) or <span className="font-mono text-xs">vendor</span> (ours).</li>
        </ul>
      </>
    ),
  },
  {
    title: "Good habits & you're done",
    icon: BookOpen,
    body: (
      <>
        <p>Rules of thumb to keep the libraries healthy:</p>
        <ul className="mt-2 space-y-1.5 text-sm list-disc list-inside">
          <li>Only <b>admins</b> should edit — sales asks admin to update.</li>
          <li>Prefer <b>edit</b> over delete. Deletion breaks in-flight drafts.</li>
          <li>Every clause and feature has a <b>version number</b> — bump it when you make a substantive change so you can trace what shipped when.</li>
          <li>Every action here is written to the <b>Audit Log</b>. You can always see who changed what.</li>
        </ul>
        <p className="mt-4 text-sm bg-emerald-50 border-l-2 border-emerald-500 p-2">✓ You now know how to run the libraries. Restart this guide anytime by clicking <b>Show guide</b> on the Libraries page.</p>
      </>
    ),
  },
];

export default function LibraryGuide({ open, onClose }) {
  const [i, setI] = useState(0);
  if (!open) return null;
  const step = STEPS[i];
  const Icon = step.icon;
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-6" data-testid="library-guide">
      <div className="bg-white border border-slate-200 w-full max-w-2xl rounded-sm flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center">
              <Icon size={18} weight="fill"/>
            </div>
            <div>
              <div className="label-micro">Tutorial · Step {i+1} of {STEPS.length}</div>
              <div className="font-display font-bold text-lg mt-0.5">{step.title}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="guide-close"><X size={20}/></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 prose prose-slate max-w-none text-sm leading-relaxed">
          {step.body}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, idx) => (
              <div key={idx} className={`h-1 w-8 rounded-full transition-colors ${idx===i ? "bg-slate-900" : idx<i ? "bg-slate-400" : "bg-slate-200"}`}/>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setI(Math.max(0, i-1))} disabled={i===0} data-testid="guide-back" className="text-sm px-3 py-1.5 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100 inline-flex items-center gap-1"><CaretLeft size={12}/>Back</button>
            {i < STEPS.length-1 ? (
              <button onClick={() => setI(i+1)} data-testid="guide-next" className="text-sm px-3 py-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm transition-colors duration-200 inline-flex items-center gap-1">Next<CaretRight size={12}/></button>
            ) : (
              <button onClick={onClose} data-testid="guide-done" className="text-sm px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm transition-colors duration-200">Got it</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
